// PhotoLightbox — shared photo viewer with ‹/› pagination.
//
// v0.36.1 — extracted so PlanScreen's defect editor can reuse it.
// v0.71.0 — `contained` mode: a sized centered MODAL (dim backdrop, page visible
//           behind) for the shared client report, instead of a full-bleed takeover.
// v0.74.0 — `contained` mode gained PAN/ZOOM: mouse-wheel zooms toward the cursor,
//           drag pans (mouse or touch), double-click toggles zoom, a Reset button
//           restores fit; paging resets the zoom. Default fullscreen mode unchanged.
//
// v0.274.0 — `area`: Brett, the viewer over a split card: "the photo viewer should
//           sit over the defect side so comms side stays open." A function
//           answering the screen box to fill ({left, top, width, height}) or
//           null; the contained viewer then covers only that box, and the
//           conversation beside it stays readable and usable.
//
// v0.279.0 — `markup` (contained only): Brett, "dont forget the ability to
//           markup and post it to conversation Just 3 tools, text, arrow and
//           pen." { onPost(photo, marks) -> Promise<{ ok, held, error }>,
//           whyNot(photo) -> string | null }. Mark up swaps the picture for
//           the drawing stage (PhotoMarkupStage); Post to conversation hands
//           the marks to the caller, which burns them into a copy and sends
//           that. The photo itself is never changed.
//
// Props: photos[{url?,thumbnailUrl?,caption?}], initialIdx, onClose, contained, area, markup.
// Keyboard: Esc close, ArrowRight/Left paginate — not while typing in a field.

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Pencil } from 'lucide-react';
import { usePanZoom } from '../utils/usePanZoom';
import { theme, btnSecondary } from '../theme';
import PhotoMarkupStage from './PhotoMarkupStage';
// 2026-09-15 — paging stops at the first and last photo (utils/photoStep).
import { stepPhoto, atFirstPhoto, atLastPhoto } from '../utils/photoStep';

export default function PhotoLightbox({ photos, initialIdx = 0, onClose, contained = false, area = null, markup = null }) {
  const list = Array.isArray(photos) ? photos.filter(p => p && (p.url || p.thumbnailUrl)) : [];
  const [idx, setIdx] = useState(Math.min(Math.max(0, initialIdx), Math.max(0, list.length - 1)));
  // v0.279.0 — mark-up: drawing, sending, and what the last post did.
  const canMark = contained && !!markup;
  const [marking, setMarking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [posted, setPosted] = useState(null); // 'sent' | 'held' | null
  const [postError, setPostError] = useState('');

  // 2026-09-14 — pan/zoom is the shared surface (usePanZoom), which claims the
  // pointer only once a drag begins. Brett: "i cant close a photo unless i am
  // fully zoomed out" — this viewer captured on pointerdown, so a press on ×
  // never became a click.
  const viewportRef = useRef(null);
  // 2026-09-15 — Brett, on a pin card: "make this card bigger and so i can zoom in
  // on photos". The in-app viewer had no zoom at all, and opened INSIDE the card:
  // the card is centred with a CSS transform, and a transformed ancestor boxes in
  // every position:fixed child — the "full screen" photo was the size of the
  // card. Both modes now render on document.body above the card, zoom with the
  // wheel, a double-click, a drag, and a pinch on a phone.
  const { isZoomed, dragging, reset: resetZoom, toggle: toggleZoom, transform, handlers, surfaceStyle } =
    usePanZoom(viewportRef, { wheel: true, pinch: true });
  const onTop = (node) => (typeof document !== 'undefined' && document.body ? createPortal(node, document.body) : node);

  // v0.274.0 — the box the viewer covers when it sits beside the conversation;
  // followed as the window resizes or the page scrolls.
  const [box, setBox] = useState(null);
  useLayoutEffect(() => {
    if (!contained || typeof area !== 'function') { setBox(null); return undefined; }
    const read = () => {
      const b = area();
      const next = b && b.width > 0 && b.height > 0
        ? { left: Math.round(b.left), top: Math.round(b.top), width: Math.round(b.width), height: Math.round(b.height) }
        : null;
      setBox(prev => (prev && next && prev.left === next.left && prev.top === next.top && prev.width === next.width && prev.height === next.height) ? prev : next);
    };
    read();
    window.addEventListener('resize', read);
    window.addEventListener('scroll', read, true);
    return () => { window.removeEventListener('resize', read); window.removeEventListener('scroll', read, true); };
  }, [contained, area]);

  useEffect(() => {
    const onKey = (e) => {
      // v0.279.0 — while marking up, the keys belong to the drawing and its
      // words; Cancel leaves it.
      if (marking) return;
      // v0.274.0 — the conversation stays usable beside the viewer: keys typed
      // in its box move the caret, and Esc there does not close the photo.
      const t = e.target;
      const typing = !!t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName || ''));
      if (typing && (e.key !== 'Escape' || box)) return;
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') setIdx(i => stepPhoto(i, 1, list.length));
      else if (e.key === 'ArrowLeft')  setIdx(i => stepPhoto(i, -1, list.length));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, list.length, box, marking]);

  // Reset zoom whenever the photo changes.
  useEffect(() => { resetZoom(); }, [idx, resetZoom]);
  useEffect(() => { setPosted(null); setPostError(''); }, [idx]);

  if (!list.length) return null;

  const cur     = list[idx];
  const url     = cur.url || cur.thumbnailUrl;
  const hasMany = list.length > 1;
  const prev    = () => setIdx(i => stepPhoto(i, -1, list.length));
  const next    = () => setIdx(i => stepPhoto(i, 1, list.length));
  const isFirst = atFirstPhoto(idx);
  const isLast  = atLastPhoto(idx, list.length);
  const counter = `${idx + 1} of ${list.length}${isLast ? ' · last photo' : ''}`;
  const whyNot = canMark && markup.whyNot ? markup.whyNot(cur) : null;
  const post = async (_mode, marks) => {
    setBusy(true); setPostError('');
    try {
      const r = await markup.onPost(cur, marks);
      if (r && r.ok) { setPosted(r.held ? 'held' : 'sent'); setMarking(false); return true; }
      setPostError((r && r.error) || 'The marked-up photo was not posted.');
      return false;
    } catch (e) {
      setPostError(`The marked-up photo was not posted: ${(e && e.message) || 'unknown error'}.`);
      return false;
    } finally {
      setBusy(false);
    }
  };

  // ── contained: sized modal with pan/zoom, keeps the report visible ──
  if (contained) {
    const roundBtn = { position: 'absolute', width: 42, height: 42, borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.9)', color: '#0f172a', fontSize: 24, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 10px rgba(0,0,0,0.25)', zIndex: 6 };
    // v0.274.0 — beside the conversation: the viewer fills the box it was given
    // and leaves the rest of the screen alone.
    const beside = !!box;
    const backdrop = beside
      ? { position: 'fixed', left: box.left, top: box.top, width: box.width, height: box.height, background: 'rgba(15,23,42,0.55)', display: 'flex', zIndex: 2000, padding: 10, boxSizing: 'border-box' }
      : { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 20 };
    const frame = beside
      ? { flex: 1, minWidth: 0 }
      : { maxWidth: 'min(880px, 95vw)', maxHeight: '92vh' };
    const view = beside
      ? { flex: 1, minHeight: 0, width: '100%' }
      : { width: 'min(880px, 95vw)', height: '70vh' };
    return onTop(
      <div data-part="photo-viewer" data-beside={beside ? 'conversation' : undefined} onClick={marking ? undefined : onClose} style={backdrop}>
        <div onClick={(e) => e.stopPropagation()} style={{ position: 'relative', background: '#fff', borderRadius: 14, boxShadow: '0 24px 70px rgba(0,0,0,0.4)', overflow: 'hidden', ...frame, display: 'flex', flexDirection: 'column', cursor: 'default' }}>
          <div
            ref={viewportRef}
            {...handlers}
            onDoubleClick={() => toggleZoom()}
            style={{ position: 'relative', ...view, background: '#0b1220', overflow: 'hidden', ...surfaceStyle, cursor: surfaceStyle.cursor || 'zoom-in', ...(marking ? { display: 'none' } : null) }}>
            <div style={{ transform, transformOrigin: '0 0', transition: dragging ? 'none' : 'transform 0.2s ease', width: '100%', height: '100%' }}>
              <img src={url} alt={cur.caption || ''} draggable={false} style={{ display: 'block', width: '100%', height: '100%', objectFit: 'contain', userSelect: 'none', pointerEvents: 'none' }} />
            </div>
            {hasMany && (<>
              {!isFirst && <button onClick={(e) => { e.stopPropagation(); prev(); }} aria-label="Previous photo" style={{ ...roundBtn, left: 10, top: '50%', transform: 'translateY(-50%)' }}>‹</button>}
              {!isLast && <button onClick={(e) => { e.stopPropagation(); next(); }} aria-label="Next photo" style={{ ...roundBtn, right: 10, top: '50%', transform: 'translateY(-50%)' }}>›</button>}
            </>)}
            {isZoomed && (
              <button onClick={(e) => { e.stopPropagation(); resetZoom(); }} aria-label="Reset zoom" style={{ position: 'absolute', bottom: 10, left: '50%', transform: 'translateX(-50%)', zIndex: 6, border: 'none', borderRadius: 6, padding: '6px 12px', background: 'rgba(15,23,42,0.72)', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Reset zoom</button>
            )}
            <button onClick={onClose} title="Close (Esc)" aria-label="Close" style={{ position: 'absolute', top: 10, right: 10, zIndex: 6, width: 36, height: 36, borderRadius: '50%', border: 'none', background: 'rgba(15,23,42,0.6)', color: 'white', fontSize: 20, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
          </div>
          {/* v0.279.0 — the drawing stage in place of the picture. The picture's
              box above stays mounted (hidden) so its wheel zoom still works after. */}
          {marking && (
            <div data-markup-stage style={{ ...view, display: 'flex', flexDirection: 'column', background: 'var(--surface-0)' }}>
              <PhotoMarkupStage
                photo={cur}
                index={0}
                count={1}
                canDraw
                startMarking
                busy={busy}
                actions={[{ mode: 'post', label: 'Post to conversation', primary: true }]}
                onCancel={() => { setMarking(false); setPostError(''); }}
                onSave={post}
                onRestore={async () => false}
              />
              {postError && <div role="alert" data-markup-error style={{ padding: '8px 16px', fontSize: 13, color: theme.danger }}>{postError}</div>}
            </div>
          )}
          {!marking && (cur.caption || hasMany || canMark) && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, padding: '10px 14px', borderTop: '1px solid #e5e7eb', background: '#fff' }}>
              <span style={{ fontSize: 13, color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: '1 1 120px', minWidth: 0 }}>{cur.caption || ''}</span>
              {hasMany && <span style={{ fontSize: 12, fontWeight: 600, color: '#94a3b8', flexShrink: 0 }}>{counter}</span>}
              {canMark && (whyNot
                ? <span data-markup-why style={{ fontSize: 12, color: theme.textLight }}>{whyNot}</span>
                : (
                  <button type="button" onClick={() => { resetZoom(); setPosted(null); setPostError(''); setMarking(true); }}
                    style={{ ...btnSecondary, display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', flexShrink: 0 }}>
                    <Pencil size={15} aria-hidden /> Mark up
                  </button>
                ))}
              {canMark && posted && (
                <span role="status" data-markup-note={posted} style={{ flexBasis: '100%', fontSize: 12, color: theme.textLight }}>
                  {posted === 'held' ? 'Held — sends when the conversation is resumed' : 'Posted to the conversation'}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── default: full screen (the in-app pin card) — zoomable, above the card ──
  const roundDark = { position: 'absolute', zIndex: 6, width: 48, height: 48, borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.18)', color: 'white', fontSize: 28, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' };
  return onTop(
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 2000 }}>
      <div
        ref={viewportRef}
        {...handlers}
        onDoubleClick={() => toggleZoom()}
        style={{ position: 'absolute', inset: 0, overflow: 'hidden', ...surfaceStyle, cursor: surfaceStyle.cursor || 'zoom-in' }}>
        <div style={{ transform, transformOrigin: '0 0', transition: dragging ? 'none' : 'transform 0.2s ease', width: '100%', height: '100%', padding: 16, boxSizing: 'border-box' }}>
          <img src={url} alt={cur.caption || ''} draggable={false} style={{ display: 'block', width: '100%', height: '100%', objectFit: 'contain', userSelect: 'none', pointerEvents: 'none' }} />
        </div>
        {hasMany && !isFirst && (
          <button onClick={(e) => { e.stopPropagation(); prev(); }} aria-label="Previous photo" style={{ ...roundDark, left: 12, top: '50%', transform: 'translateY(-50%)' }}>‹</button>
        )}
        {hasMany && !isLast && (
          <button onClick={(e) => { e.stopPropagation(); next(); }} aria-label="Next photo" style={{ ...roundDark, right: 12, top: '50%', transform: 'translateY(-50%)' }}>›</button>
        )}
        <div style={{ position: 'absolute', zIndex: 6, bottom: 18, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 8, alignItems: 'center' }}>
          {hasMany && (
            <span style={{ color: 'white', fontSize: 12, fontWeight: 600, background: 'rgba(0,0,0,0.55)', padding: '4px 10px', borderRadius: 999 }}>{counter}</span>
          )}
          {isZoomed ? (
            <button onClick={(e) => { e.stopPropagation(); resetZoom(); }} aria-label="Reset zoom" style={{ border: 'none', borderRadius: 999, padding: '5px 12px', background: 'rgba(255,255,255,0.9)', color: '#0f172a', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Reset zoom</button>
          ) : (
            <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: 11, background: 'rgba(0,0,0,0.45)', padding: '4px 10px', borderRadius: 999 }}>Pinch or double-click to zoom</span>
          )}
        </div>
        <button onClick={(e) => { e.stopPropagation(); onClose(); }} aria-label="Close" title="Close (Esc)" style={{ ...roundDark, top: 14, right: 14, width: 42, height: 42, fontSize: 22 }}>×</button>
      </div>
    </div>
  );
}
